import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertCircle, MessageSquareWarning } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const R = (n) => n == null ? '—' : 'R ' + Number(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * UnpaidLeaveBreakdown — Card showing every leave request that had unpaid
 * days deducted, with a "Dispute" button per row.
 */
export default function UnpaidLeaveBreakdown({ staffId }) {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disputeRow, setDisputeRow] = useState(null);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    if (!staffId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/leave/unpaid-breakdown/${staffId}`, { headers });
      setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffId]);

  useEffect(() => { fetch(); }, [fetch]);

  const submitDispute = async () => {
    if (!disputeRow || !message.trim()) return toast.error('Please describe why the deduction is wrong');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/leave/dispute-unpaid`, {
        request_id: disputeRow.request_id,
        message: message.trim(),
      }, { headers });
      toast.success('Dispute filed — admin will review');
      setDisputeRow(null); setMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="text-center py-6 text-muted-foreground">Loading unpaid leave…</CardContent>
      </Card>
    );
  }
  if (!data || !data.rows?.length) {
    return (
      <Card data-testid="unpaid-empty">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-emerald-600" />
            Unpaid Leave Deductions
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-emerald-800 bg-emerald-50 rounded p-3">
          ✓ You have no unpaid leave deductions on record.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="unpaid-breakdown-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-rose-600" />
          Unpaid Leave Deductions
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            Total: <b className="text-rose-700">{data.total_unpaid_days.toFixed(2)} days</b>
            {data.daily_rate_used > 0 && <> · daily rate <b>{R(data.daily_rate_used)}</b></>}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded bg-amber-50 border border-amber-200 text-xs text-amber-900 p-3">
          {data.note}
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Leave dates</TableHead>
                <TableHead className="text-center">Total days</TableHead>
                <TableHead className="text-center">Paid</TableHead>
                <TableHead className="text-center">Unpaid</TableHead>
                <TableHead className="text-right">Deduction</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow key={r.request_id} data-testid={`unpaid-row-${r.request_id}`}>
                  <TableCell className="text-xs font-mono">
                    {r.start_date}<br />→ {r.end_date}
                  </TableCell>
                  <TableCell className="text-center">{r.total_days}</TableCell>
                  <TableCell className="text-center text-emerald-700">{r.paid_days}</TableCell>
                  <TableCell className="text-center font-bold text-rose-600">{r.unpaid_days}</TableCell>
                  <TableCell className="text-right font-semibold text-rose-700">{R(r.deduction_amount)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-xs truncate">{r.reason || '—'}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-400 text-amber-700 hover:bg-amber-50"
                      onClick={() => { setDisputeRow(r); setMessage(''); }}
                      data-testid={`dispute-${r.request_id}`}
                    >
                      <MessageSquareWarning className="h-3 w-3 mr-1" /> Dispute
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <Dialog open={!!disputeRow} onOpenChange={(o) => !o && setDisputeRow(null)}>
        <DialogContent data-testid="dispute-dialog">
          <DialogHeader>
            <DialogTitle>Dispute unpaid leave deduction</DialogTitle>
            <DialogDescription>
              Filing a note against leave <b>{disputeRow?.start_date} → {disputeRow?.end_date}</b> ({disputeRow?.unpaid_days} unpaid days, {R(disputeRow?.deduction_amount)}). Admin will review.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-sm font-medium">Why is this deduction wrong?</label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. I was on approved sick leave, not annual…"
              rows={4}
              data-testid="dispute-message"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeRow(null)}>Cancel</Button>
            <Button onClick={submitDispute} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="dispute-submit">
              {saving ? 'Submitting…' : 'Submit dispute'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
